import React, { memo, useCallback, useEffect, useMemo } from "react";

// internal deps
import { useEffectOnce } from "@swiggy-private/core-ui";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { default as ArrowBack } from "@swiggy-private/dls-web-icons/dist/web/generic/arrowBack/20";
import { isEmpty } from "@swiggy-private/js-utils";

// components
import DineCashLottie from "./DineCashLottie";
import { PoolingLedger } from "./components/Ledger";
import QRWidgetSection from "./components/QRWidgetSection/QRWidgetSection";

// test ids
import { DINE_CASH_POOLING_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";

// styles
import {
    Container,
    ScrollableContent,
    TopSection,
    Header,
    ContentWrapper,
    HowItWorksCard,
    HowItWorksHeader,
    HowItWorksTitle,
    HowItWorksBody,
    StepList,
    StepItem,
    StepNumberColumn,
    StepCircle,
    StepConnector,
    StepText,
    DoneButtonWrapper,
} from "./styles";

// types
import { IDineCashPoolingProps } from "./types";

// constants
import { DINE_CASH_DONE_BUTTON_TEXT, DINE_CASH_HOW_IT_WORKS_TITLE } from "./constants";

// helpers
import { formatText, getScreenImpressionState, isMaxPoolAmountReached } from "./helpers";

const DineCashPooling = ({
    qrCodeUrl,
    bulletPoints,
    lottieUrl,
    onWhatsAppShare,
    onDoneBtnClick,
    onBackClick,
    totalAmountReceived,
    maxPoolAmountAllowed,
    maxPerPersonCap,
    senders,
    popupAsset,
    analytics,
}: IDineCashPoolingProps) => {
    const poolingStarted = useMemo(() => !isEmpty(senders) && !isEmpty(lottieUrl), [senders, lottieUrl]);

    const maxAmountReached = useMemo(
        () => isMaxPoolAmountReached(totalAmountReceived, maxPoolAmountAllowed),
        [totalAmountReceived, maxPoolAmountAllowed],
    );

    const impressionState = useMemo(
        () => getScreenImpressionState(maxAmountReached, totalAmountReceived),
        [maxAmountReached, totalAmountReceived],
    );

    const handleDoneBtnClick = useCallback(() => {
        analytics?.onDoneButtonClick?.(DINE_CASH_DONE_BUTTON_TEXT);
        onDoneBtnClick();
    }, [analytics, onDoneBtnClick]);

    useEffectOnce(() => {
        analytics?.onDoneButtonImpression?.(DINE_CASH_DONE_BUTTON_TEXT);
    });

    useEffect(() => {
        analytics?.onScreenImpression?.(totalAmountReceived ?? 0, impressionState);
    }, [totalAmountReceived, maxAmountReached, analytics, impressionState]);

    return (
        <Container data-testid={DINE_CASH_POOLING_TEST_IDS.CONTAINER}>
            <ScrollableContent>
                <TopSection poolingStarted={poolingStarted} hasLottie={!isEmpty(lottieUrl)}>
                    <DineCashLottie
                        url={lottieUrl}
                        totalAmountReceived={totalAmountReceived}
                        maxPoolAmountAllowed={maxPoolAmountAllowed}
                        maxAmountReached={maxAmountReached}
                    />
                    <Header>
                        <GenericIcon
                            fillColor="text_Highest_Emphasis"
                            strokeColor="text_Highest_Emphasis"
                            onClick={onBackClick}
                            Icon={ArrowBack}
                            data-testid={DINE_CASH_POOLING_TEST_IDS.BACK_BUTTON}
                        />
                    </Header>
                </TopSection>
                <ContentWrapper>
                    <PoolingLedger
                        senders={senders}
                        popupAsset={popupAsset}
                        maxPoolAmountReached={maxAmountReached}
                        onLedgerImpression={analytics?.onLedgerPoolingImpression}
                        onLedgerHeaderImpression={analytics?.onPoolingGratificationImpression}
                    />
                    <QRWidgetSection
                        qrCodeImageUrl={qrCodeUrl}
                        onWhatsAppShare={onWhatsAppShare}
                        onQrCodeImpression={analytics?.onQrCodeImpression}
                        onWhatsAppShareImpression={analytics?.onWhatsAppShareImpression}
                        onWhatsAppShareClickEvent={analytics?.onWhatsAppShareClickEvent}
                        maxAmountReached={maxAmountReached}
                    />

                    {!isEmpty(bulletPoints) && (
                        <HowItWorksCard data-testid={DINE_CASH_POOLING_TEST_IDS.BULLET_LIST}>
                            <HowItWorksHeader>
                                <HowItWorksTitle variant="V2.overline.neutral.bold_spaced" color="rating_Excellent">
                                    {DINE_CASH_HOW_IT_WORKS_TITLE}
                                </HowItWorksTitle>
                            </HowItWorksHeader>
                            <HowItWorksBody>
                                <StepList>
                                    {bulletPoints.map((point, index) => (
                                        <StepItem key={index} data-testid={DINE_CASH_POOLING_TEST_IDS.BULLET_ITEM}>
                                            <StepNumberColumn>
                                                <StepCircle>
                                                    <Text variant="V2.caption.neutral.black" color="background_Primary">
                                                        {index + 1}
                                                    </Text>
                                                </StepCircle>
                                                <StepConnector visible={index < bulletPoints.length - 1} />
                                            </StepNumberColumn>
                                            <StepText variant="V2.body2.neutral.regular" color="text_High_Emphasis">
                                                {formatText(point, maxPerPersonCap, maxPoolAmountAllowed)}
                                            </StepText>
                                        </StepItem>
                                    ))}
                                </StepList>
                            </HowItWorksBody>
                        </HowItWorksCard>
                    )}
                </ContentWrapper>
            </ScrollableContent>
            <DoneButtonWrapper>
                <FullWidthButton
                    variant="positive"
                    onClick={handleDoneBtnClick}
                    data-testid={DINE_CASH_POOLING_TEST_IDS.DONE_BUTTON}
                >
                    {DINE_CASH_DONE_BUTTON_TEXT}
                </FullWidthButton>
            </DoneButtonWrapper>
        </Container>
    );
};

export default memo(DineCashPooling);
